import { useEffect, useState } from 'react';
import { Input as RoccoInput } from '@rocco/components/input';
import { Slider } from '@rocco/components/slider/slider';
import type { FilterConfigModalItemPrice } from '@rocco/ui/filter-sort/types/filter-config-modal';
import type { RangeFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/range-filter-state';
import type { FilterModalElementProps } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModalElement';

export const FiltersModalPrice = (
    props: FilterModalElementProps<
        FilterConfigModalItemPrice,
        RangeFilterState
    >,
) => {
    const { filterState, onFilterStateUpdate } = props;
    const [localRange, setLocalRange] = useState([
        filterState.value.min,
        filterState.value.max,
    ]);

    const handleSliderChange = (newRange: [number, number]) => {
        onFilterStateUpdate({
            ...filterState,
            value: {
                min: Math.min(
                    Math.max(filterState.value.min, newRange[0]),
                    filterState.value.max,
                ),
                max: Math.max(
                    Math.min(filterState.value.max, newRange[1]),
                    filterState.value.min,
                ),
            },
        });
    };

    useEffect(() => {
        if (
            filterState.value.min !== localRange[0] ||
            filterState.value.max !== localRange[1]
        ) {
            setLocalRange([filterState.value.min, filterState.value.max]);
        }
        // we don't need to trigger this effect when local state is changed
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterState.value.min, filterState.value.max]);

    if (filterState.value.min === filterState.value.max) {
        return null;
    }

    return (
        <div className="flex flex-col flex-wrap gap-2 pt-2">
            <div className="px-3 pt-6 pb-2">
                <Slider
                    value={localRange}
                    min={filterState.range.min}
                    max={filterState.range.max}
                    onValueChange={setLocalRange}
                    onValueCommit={handleSliderChange}
                    step={1}
                />
            </div>

            <div className="flex flex-wrap gap-2 pt-2 justify-between">
                <div className="w-32">
                    <span className="text-2.5 font-medium tracking-wider text-gray-900 hidden">
                        MINIMUM
                    </span>
                    <RoccoInput
                        type="number"
                        prefix="$"
                        color="blackOutline"
                        value={(localRange[0] ?? 0) / filterState.rangeDivisor}
                        onChange={e =>
                            setLocalRange([
                                (parseInt(e.currentTarget.value) || 0) *
                                    filterState.rangeDivisor,
                                localRange[1] ?? 0,
                            ])
                        }
                    />
                </div>

                <div className="w-8 items-center justify-center pt-4 hidden">
                    <div className="h-px w-4 bg-gray-900" />
                </div>

                <div className="w-32">
                    <span className="text-2.5 font-medium tracking-wider text-gray-900 hidden">
                        MAXIMUM
                    </span>
                    <RoccoInput
                        type="number"
                        prefix="$"
                        color="blackOutline"
                        value={(localRange[1] ?? 0) / filterState.rangeDivisor}
                        onChange={e =>
                            setLocalRange([
                                localRange[0] ?? 0,
                                (parseInt(e.currentTarget.value) || 0) *
                                    filterState.rangeDivisor,
                            ])
                        }
                    />
                </div>
            </div>
        </div>
    );
};
